
/**
 * 延迟 resolve 数据
 * @param data 数据
 * @param delay 延迟时间（单位：毫秒）, 默认值 3000
 * @returns
 */
export function resolve<T>(data: T, delay = 1000): Promise<T> {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(data)
        }, delay)
    })
}

export class Lazy<T> {
    private _hasCache = false
    private _cacheValue!: T

    constructor(private fn: () => T) {}

    get Value() {
        if (!this._hasCache) {
            this._hasCache = true
            this._cacheValue = this.fn()
        }
        return this._cacheValue
    }

    static from<T>(fn: () => T) {
        return new Lazy(fn)
    }

    static of<T>(value: T) {
        return new Lazy(() => value)
    }
}

/**
 * Accessor Singleton Decorators
 */
export function singleton() {
    return function<T>(target: any, _propertyKey: string, descriptor: PropertyDescriptor) {
        const get = descriptor.get as () => T
        let result: T
        let hasCache = false
        descriptor.get = function() {
            if (!hasCache) {
                result = get.call(target)
                hasCache = true
            }
            return result
        }
    }
}

export type PromiseType<T, K> = (arg: K[]) => Promise<T>

export function batchProxy<T, K>(fn: PromiseType<T, K>, delay = 1000) {
    const cache: K[] = []

    let timer: number | null

    let promise: Promise<T>

    return (codes: K[]) => {
        cache.push(...codes)

        if (timer) {
            return promise
        }

        promise = new Promise<T>((resolve, reject) => {
            timer = window.setTimeout(() => {
                fn(cache)
                    .then(resolve)
                    .catch(reject)
                clearTimeout(timer as number)
                timer = null
                cache.length = 0
            }, delay)
        })

        return promise
    }
}

/**
 * 读取文件数据
 * @param blob
 * @returns
 */
export function readAsDataURL(blob: Blob) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('loadend', () => {
            resolve(reader.result as string)
        })
        reader.addEventListener('error', () => {
            reject(reader.error)
        });
        reader.addEventListener('abort', () => {
            reject(reader.error)
        })
        reader.readAsDataURL(blob)
    })
}

/**
 * 下载文件
 * @param src 下载链接
 */
export function createIframeDownload(src: string) {
    if (!src) return;
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none';
    iframe.setAttribute('src', src)
    document.body.appendChild(iframe);
    /**
     * Firefox 会触发 load 事件，但是如果此时删除 iframe，文件将不会被下载
     * Chrome 不会触发 load 事件
     * 由于以上情况，现在改为 1 分钟以后删除 iframe
     */
    setTimeout(() => {
        document.body.removeChild(iframe);
        iframe.remove();
    }, 60 * 1000);
}
